import { Button } from "@/components/ui/button";
import { WarningCircle, ArrowsClockwise } from "@phosphor-icons/react";

/**
 * Phase N1A — anti-faux-état-vide banner.
 *
 * Renders ONLY when `error === true`. Stays out of the way otherwise so
 * dashboards can keep showing previously-loaded data. The banner is
 * intentionally discreet (one row, no modal, no blocking overlay) so the
 * user can still use the page while the issue is being resolved.
 *
 * Props:
 *   error    — boolean. When true the banner is shown.
 *   onRetry  — optional callback. When provided, a "Réessayer" button is
 *              displayed and invokes the callback on click.
 *   message  — optional override of the default text. Default text is
 *              tuned for "can't reach backend" scenarios.
 *
 * No translation hook here — kept dependency-free to remain safe to drop
 * into pages that don't import i18n. If we later want localisation we can
 * pass `message` via i18n at the call site.
 */
export function LoadErrorBanner({ error, onRetry, message }) {
  if (!error) return null;
  return (
    <div
      data-testid="load-error-banner"
      role="alert"
      className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200"
    >
      <div className="flex items-center gap-2 min-w-0">
        <WarningCircle size={18} weight="bold" className="shrink-0" />
        <span className="truncate">
          {message || "Impossible de charger les données. Vérifiez votre connexion ou réessayez."}
        </span>
      </div>
      {onRetry && (
        <Button
          data-testid="load-error-retry"
          size="sm"
          variant="ghost"
          onClick={onRetry}
          className="h-8 shrink-0 border border-amber-500/40 text-amber-200 hover:bg-amber-500/20"
        >
          <ArrowsClockwise size={14} className="mr-1.5" />
          Réessayer
        </Button>
      )}
    </div>
  );
}
